/** Why a spectate socket did not open, in words the page can print.
 *
 * A browser's `WebSocket` tells you almost nothing when a connection fails.
 * There is no status, no body, no reason — an upgrade refused with `403
 * type_disabled` and a server that is simply down both arrive as the same
 * bare `close` event. That is no basis for a screen that has to distinguish
 * "this driver is not on track", "this stream is private" and "the service
 * has switched live off", so the page asks the ordinary way first.
 *
 * What it asks is `GET /api/live`, not the spectate path. The obvious probe —
 * a plain `GET` of `/v1/live/{user}`, reading `426 upgrade_required` as the
 * go-ahead — cannot work against this service: `src/routes/live.ts` refuses a
 * non-upgrade *before* it looks the account up, so every address answers 426,
 * including a private one, a driver who has never streamed and an id that
 * does not exist. A probe that reads 426 as "watchable" therefore always says
 * yes, the socket is opened, the upgrade is refused with no readable reason,
 * and the page settles into "reconnecting" for ten minutes — which is the one
 * state the screen exists to avoid.
 *
 * The listing answers it properly, and answers it by construction rather than
 * by luck: `GET /api/live` returns the streams *this visitor* may watch — the
 * public ones, their own, and everybody's if they administer the service —
 * which is the same set the spectate socket will open. So a stream in the
 * listing is a stream that will connect, and the server's live toggle arrives
 * in the same reply.
 *
 * What the listing cannot say is *why* an address is missing from it: a
 * private stream and an idle driver look identical from outside. So when the
 * listing does not carry the stream the spectate path is asked directly, in
 * case this deployment can name the reason — today it answers 426 to that too
 * and the page says both possibilities rather than picking one, which is the
 * honest reading of an answer the service did not give.
 *
 * The retry policy falls out of the same answer. A refusal that is about the
 * service's configuration will not change because we asked again, so that one
 * stops. "Nobody is streaming there" will change the moment the driver starts
 * their engine, so that one keeps asking — with exponential backoff, and not
 * for ever: a link to a driver who never streams should end up as a quiet
 * button rather than a poll that runs until the tab is closed.
 */

import { ApiError, api, getLiveListing } from '@/lib/api';
import type { LiveListing } from '@/lib/types';

export type LiveStatus =
  /** No stream has been chosen — `/live` without an account id. */
  | 'idle'
  /** Asking whether the stream can be watched, or opening the socket. */
  | 'connecting'
  /** Frames are arriving. */
  | 'live'
  /** The socket dropped and another attempt is scheduled. */
  | 'reconnecting'
  /** The driver stopped, or an admin stopped them. */
  | 'ended'
  /** Nobody is streaming at that address right now, and we know that. */
  | 'offline'
  /** The stream is not public and this visitor is not its owner. */
  | 'private'
  /** There is nothing to watch there and the service did not say which of
   *  "not streaming" and "not public" it is. Both are said on screen. */
  | 'unavailable'
  /** The server is not carrying live streams at all. */
  | 'disabled'
  /** Anything else, including the network being gone. */
  | 'error';

/** A refusal, classified. `retry` is whether asking again could ever help. */
export interface LiveFault {
  status: LiveStatus;
  code: string;
  reason: string;
  retry: boolean;
}

/** Who is watching, so far as the access rules care: a driver may always
 *  watch their own stream and an admin may watch anyone's, which is what
 *  turns "not in the listing" into a certainty rather than a maybe. */
export interface LiveViewer {
  id: string;
  admin: boolean;
}

/** First delay, and the ceiling. Eight hundred milliseconds is short enough
 *  that an ordinary blip is invisible; fifteen seconds is long enough that a
 *  driver who has gone to make tea is not being polled at. */
export const BACKOFF_BASE_MS = 800;
export const BACKOFF_CAP_MS = 15_000;

/** Attempts before a stream that has never appeared is left alone. At the
 *  cap that is a little over ten minutes of waiting. */
export const MAX_ATTEMPTS = 48;

/**
 * How long to wait before attempt `attempt` (1 is the first retry).
 *
 * Doubling, capped, and pulled down by up to a quarter so that a hundred
 * spectators of the same stream do not all come back at the same instant.
 * `jitter` is a number in [0, 1) — the caller passes `Math.random()`, and a
 * test passes a fixed one.
 */
export function backoffDelay(attempt: number, jitter = 0): number {
  const step = Math.max(1, Math.floor(attempt));
  const raw = Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** (step - 1));
  return Math.round(raw * (1 - 0.25 * Math.min(Math.max(jitter, 0), 0.999)));
}

/** Nothing to watch, and no answer as to why. Said in full rather than
 *  guessed at: claiming "off air" about a stream that is running privately is
 *  as wrong as claiming "private" about a driver who is simply not out. */
export const UNRESOLVED: LiveFault = {
  status: 'unavailable',
  code: 'not_listed',
  reason: 'nobody is streaming at that address, or their stream is not public',
  retry: true,
};

/**
 * What a refusal of an ordinary `GET` means.
 *
 * `null` means the service declined to say anything useful — which is what
 * `426 upgrade_required` is, since this service answers it to every spectate
 * address before it has looked at the account. It is not permission to open
 * the socket; only the listing gives that.
 */
export function classifyProbe(thrown: unknown): LiveFault | null {
  if (thrown instanceof ApiError) {
    if (thrown.status === 426) return null;

    if (thrown.status === 403) {
      return thrown.code === 'type_disabled'
        ? { status: 'disabled', code: thrown.code, reason: thrown.reason, retry: false }
        : { status: 'private', code: thrown.code, reason: thrown.reason, retry: false };
    }
    if (thrown.status === 401) {
      return { status: 'private', code: thrown.code, reason: thrown.reason, retry: false };
    }
    if (thrown.status === 404) {
      return { status: 'offline', code: thrown.code, reason: thrown.reason, retry: true };
    }
    return {
      status: 'error',
      code: thrown.code,
      reason: thrown.reason,
      retry: thrown.status >= 500 || thrown.status === 429,
    };
  }

  const message = thrown instanceof Error ? thrown.message : String(thrown);
  return { status: 'error', code: 'unreachable', reason: message, retry: true };
}

/** `'watchable'` is the go-ahead; `'ask'` means the listing did not settle it
 *  and the spectate path is worth a direct question. */
export type ListingVerdict = LiveFault | 'watchable' | 'ask';

/**
 * What `GET /api/live` settles about one address.
 *
 * The listing is authoritative twice over. It carries the server's live
 * toggle, and it carries exactly the streams this visitor is allowed to open
 * a socket to — so a stream that is in it will connect, and a stream that is
 * not will not. For a visitor who can see private streams at that address —
 * their own, or an admin's view of anybody's — "not in the listing" is the
 * whole answer: nobody is driving there. For everybody else it is half of
 * one, and the rest is asked elsewhere.
 */
export function readListing(
  streamId: string,
  listing: LiveListing,
  viewer?: LiveViewer | null,
): ListingVerdict {
  if (listing.enabled === false) {
    return {
      status: 'disabled',
      code: 'type_disabled',
      reason: 'this server is not carrying live streams',
      retry: false,
    };
  }

  if ((listing.streams ?? []).some((stream) => stream.user_id === streamId)) return 'watchable';

  if (viewer && (viewer.admin || viewer.id === streamId)) {
    return {
      status: 'offline',
      code: 'not_streaming',
      reason: 'that driver is not streaming',
      retry: true,
    };
  }

  return 'ask';
}

/**
 * Ask whether the stream can be watched, without opening a socket.
 *
 * The listing first, because it is the answer; the spectate path only when
 * the listing came up empty, and only to see whether this deployment will
 * name the reason. A success from that second request would be surprising —
 * the endpoint has nothing to answer a plain `GET` with — so it is treated as
 * a refusal with no reason, which is exactly how the page should read it.
 */
export async function probeStream(
  streamId: string,
  viewer?: LiveViewer | null,
): Promise<LiveFault | null> {
  let listing: LiveListing;
  try {
    listing = await getLiveListing();
  } catch (thrown) {
    return classifyProbe(thrown) ?? UNRESOLVED;
  }

  const verdict = readListing(streamId, listing, viewer);
  if (verdict === 'watchable') return null;
  if (verdict !== 'ask') return verdict;

  try {
    await api.get<unknown>(`/v1/live/${encodeURIComponent(streamId)}`);
    return { status: 'error', code: 'unexpected', reason: 'the stream did not upgrade', retry: true };
  } catch (thrown) {
    return classifyProbe(thrown) ?? UNRESOLVED;
  }
}

/** Close codes `src/live/stream.ts` owns. 1000 is the ordinary one: the
 *  stream is over and coming back would only find it over again. */
export const CLOSE_ENDED = 1000;
export const CLOSE_RUBBISH = 4400;
export const CLOSE_KICKED = 4403;

/** Whether a closed socket is worth reopening. */
export function shouldReconnect(code: number): boolean {
  return code !== CLOSE_ENDED && code !== CLOSE_KICKED && code !== CLOSE_RUBBISH;
}

/** A socket that closed without ever opening.
 *
 * "Reconnecting" is a claim that there was once a connection, so it may not
 * be made here. The listing said the stream was there and the upgrade was
 * refused anyway, which is a race with a stream that has just ended or a
 * genuine fault; either way the page says what happened and the next probe
 * replaces it with whatever the listing says by then. */
export const REFUSED: LiveFault = {
  status: 'error',
  code: 'refused',
  reason: 'the stream did not accept the connection',
  retry: true,
};
